import Tooltip from "@/v2-ui/tooltip"
import { DotText } from "@/v2-backoffice-client/components/dot"
import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"
import {
  PAYMENT_HEALTH_SETTLED,
  getPaymentHealthConfig,
} from "@/v2-backoffice-client/customer/billing/customerBilling.constants"
import { formatInvoiceAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"

export type CustomerPaymentBadgeProps = {
  customer: Pick<
    CustomerFragment,
    | "PaymentHealth"
    | "OutstandingBalance"
    | "OverdueBalance"
    | "PrimaryCurrency"
  >
}

// CustomerPaymentBadge surfaces whether the customer is behind on billing
// (server-derived PaymentHealth on the customer row, worst invoice state
// governing). The healthy Settled state renders as a plain dot; every other
// state gets a tooltip with the amounts behind it and what to do. Renders
// nothing for customers with no invoice history.
function CustomerPaymentBadge(props: CustomerPaymentBadgeProps) {
  const { customer } = props

  const health = customer.PaymentHealth
  const config = getPaymentHealthConfig(health)
  if (!config) return null

  if (health === PAYMENT_HEALTH_SETTLED) {
    return <DotText variant={config.variant}>{config.label}</DotText>
  }

  return (
    <Tooltip>
      <Tooltip.Trigger asChild>
        <DotText variant={config.variant}>{config.label}</DotText>
      </Tooltip.Trigger>

      <Tooltip.Content side="top" className="ui-tooltip-content--panel">
        <div className="flex max-w-xs flex-col gap-1 p-3">
          <DotText
            variant={config.variant}
            className="flex font-medium text-popover-foreground"
          >
            {config.label}
          </DotText>
          <p className="text-muted-foreground">{config.description}</p>

          {customer.OutstandingBalance != null &&
            customer.OutstandingBalance > 0 && (
              <p className="mt-1 text-xs">
                <span className="text-muted-foreground">Outstanding: </span>
                {formatInvoiceAmount(
                  customer.OutstandingBalance,
                  customer.PrimaryCurrency,
                )}
                {customer.OverdueBalance != null &&
                  customer.OverdueBalance > 0 && (
                    <>
                      <span className="text-muted-foreground">
                        {" "}
                        · overdue:{" "}
                      </span>
                      {formatInvoiceAmount(
                        customer.OverdueBalance,
                        customer.PrimaryCurrency,
                      )}
                    </>
                  )}
              </p>
            )}

          {config.fix && (
            <p className="mt-1 text-xs text-muted-foreground">{config.fix}</p>
          )}
        </div>
      </Tooltip.Content>
    </Tooltip>
  )
}

export default CustomerPaymentBadge
